
import { useState, useEffect, useSyncExternalStore } from "react";
import { Plus, Search, User, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetCoins } from "@/contexts/GetCoinsContext";
import { RedeemModal } from "./RedeemModal";
import { CurrencySlider } from "./CurrencySlider";
import { useBalance } from "@/contexts/BalanceContext";
import { MobileSearchOverlay } from "./MobileSearchOverlay";
import { prefetchPackages } from "@/lib/prefetch-packages";
import { useJackpot } from "@/contexts/JackpotContext";
import { GCBadgeSVG, SCBadgeSVG, CurrencyBadge } from "./CurrencyBadge";
import { useVerification } from "@/contexts/VerificationContext";
import { useChatOptional } from "@/contexts/ChatContext";
import { useAuth } from "@/contexts/AuthContext";

/** Breakpoint below which Get Coins / Redeem move into the sidebar */
const COMPACT_HEADER_BREAKPOINT_CHAT_OPEN = 1310;
const COMPACT_HEADER_BREAKPOINT_CHAT_CLOSED = 1050;

function subscribeToResize(cb: () => void) {
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}
function getWidth() { return window.innerWidth; }
function useWindowWidth() { return useSyncExternalStore(subscribeToResize, getWidth, () => 1400); }

interface CasinoHeaderProps {
  /** Whether the desktop sidebar is collapsed — when collapsed, buttons stay in header */
  isSidebarCollapsed?: boolean;
}

export const CasinoHeader = ({ isSidebarCollapsed = false }: CasinoHeaderProps = {}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCurrency, setSelectedCurrency, formatCurrency } = useTheme();
  const { openGetCoinsModal } = useGetCoins();
  const { balance } = useBalance();
  const { getJackpotsForCurrency } = useJackpot();
  const { isVerified } = useVerification();
  const isDataVerified = isVerified('data');
  const { session } = useAuth();
  const isAuthenticated = !!session;
  const chatContext = useChatOptional();
  const windowWidth = useWindowWidth();
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Buttons move to sidebar when viewport is narrow enough (threshold depends on chat state)
  const breakpoint = chatContext?.isOpen ? COMPACT_HEADER_BREAKPOINT_CHAT_OPEN : COMPACT_HEADER_BREAKPOINT_CHAT_CLOSED;
  const buttonsInSidebar = windowWidth < breakpoint && !isSidebarCollapsed;
  
  const jackpots = getJackpotsForCurrency(selectedCurrency);
  
  // Format jackpot amount - always 2 decimal precision
  // GC: Always abbreviated (K/M/B), SC: Shows full up to 99,999.99
  const formatJackpotAmount = (amount: number) => {
    const value = amount || 0;
    
    // Helper to format with commas and always 2 decimal places
    const formatWithDecimals = (num: number) => {
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    
    // Abbreviate large numbers - always show 2 decimal places
    if (value >= 1_000_000_000) {
      return (value / 1_000_000_000).toFixed(2) + 'B';
    } else if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(2) + 'M';
    } else if (selectedCurrency === 'GC' && value >= 1_000) {
      return (value / 1_000).toFixed(2) + 'K';
    } else if (selectedCurrency === 'SC' && value >= 100_000) {
      return (value / 1_000).toFixed(2) + 'K';
    }
    
    // Show full number with commas and always 2 decimal places
    return formatWithDecimals(value);
  };

  // Prefetch packages on mount (with slight delay to not block initial render)
  useEffect(() => {
    const timer = setTimeout(() => {
      prefetchPackages();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  
  const isLobby = location.pathname === '/' || location.pathname === '' || location.pathname === '/home';
  const isGamePlayPage = location.pathname.startsWith('/play/');
  
  // Pages that show games and should have search functionality (desktop)
  const gamePages = ['/', '/home', '/endless-reels', '/for-you', '/live-experience', '/high-play'];
  
  // Desktop: show search bar on game pages only
  const shouldShowSearch = isLobby || gamePages.includes(location.pathname);
  // Mobile: show search icon on all pages except game play
  const shouldShowMobileSearch = !isGamePlayPage;

  const getTotalSC = () => {
    if (!balance) return 0;
    const safeNumber = (val: any): number => {
      const num = Number(val);
      return typeof num === 'number' && !isNaN(num) ? num : 0;
    };
    
    const scBonus = safeNumber(balance.sc_bonus);
    const scPurchased = safeNumber(balance.sc_purchased);
    const scRedeemable = safeNumber(balance.sc_redeemable);
    return scBonus + scPurchased + scRedeemable;
  };

  const isGC = selectedCurrency === 'GC';

  return (
    <>
      {/* Mobile: Native App Header */}
      <header 
        className={`lg:hidden fixed top-0 left-0 right-0 z-50 w-full transition-colors duration-300 pointer-events-none pt-[env(safe-area-inset-top)]`}
        style={{ 
          background: isGamePlayPage 
            ? 'transparent' 
            : 'linear-gradient(180deg, hsl(var(--background) / 0.98) 0%, hsl(var(--card) / 0.96) 100%)'
        }}
      >
        {/* Main header row */}
        <div className="w-full px-3 py-2.5 pointer-events-auto">
          <div className="flex items-center gap-2.5">
            
            {/* Left icon — Profile (if not data verified) or Search */}
            {shouldShowMobileSearch && (
              isDataVerified ? (
                <button
                  onClick={() => setIsMobileSearchOpen(true)}
                  className={`flex items-center justify-center h-11 w-11 rounded-full transition-all flex-shrink-0 active:scale-95 ${
                    isGC
                      ? 'bg-amber-500/10 text-amber-400 active:bg-amber-500/20 ring-1 ring-amber-500/20'
                      : 'bg-emerald-500/10 text-emerald-400 active:bg-emerald-500/20 ring-1 ring-emerald-500/20'
                  }`}
                >
                  <Search className="h-5 w-5" />
                </button>
              ) : (
                <button
                  onClick={() => navigate('/profile')}
                  className={`flex items-center justify-center h-11 w-11 rounded-full transition-all flex-shrink-0 active:scale-95 ${
                    isGC
                      ? 'bg-amber-500/10 text-amber-400 active:bg-amber-500/20 ring-1 ring-amber-500/20'
                      : 'bg-emerald-500/10 text-emerald-400 active:bg-emerald-500/20 ring-1 ring-emerald-500/20'
                  }`}
                >
                  <User className="h-5 w-5" />
                </button>
              )
            )}

            {/* Balance pill — larger touch targets, native feel */}
            <div className={`flex items-center gap-2 flex-1 min-w-0 rounded-full px-2 py-1.5 transition-colors duration-300 ${
              isGC
                ? 'bg-amber-500/[0.08] ring-1 ring-amber-400/40'
                : 'bg-emerald-500/[0.08] ring-1 ring-emerald-400/40'
            }`}>
              <div className="flex items-center gap-2 pl-0.5 min-w-0">
                {isGC ? <GCBadgeSVG size={28} /> : <SCBadgeSVG size={28} />}
                <span className="text-foreground font-bold text-base tabular-nums truncate">
                  {formatCurrency(
                    isGC ? (balance?.gc_balance || 0) : getTotalSC(),
                    selectedCurrency
                  )}
                </span>
              </div>
              <Button
                onClick={openGetCoinsModal}
                onPointerEnter={prefetchPackages}
                size="icon"
                className={`h-8 w-8 rounded-full border-0 flex-shrink-0 p-0 ml-auto active:scale-95 transition-transform ${
                  isGC
                    ? 'bg-amber-500 hover:bg-amber-400 shadow-[0_2px_8px_rgba(245,158,11,0.3)]'
                    : 'bg-emerald-500 hover:bg-emerald-400 shadow-[0_2px_8px_rgba(16,185,129,0.3)]'
                }`}
              >
                <Plus className="h-4.5 w-4.5 text-white" strokeWidth={3} />
              </Button>
            </div>

            {/* Currency toggle — solid colored track like the original, bigger for easy tapping */}
            <button
              onClick={() => setSelectedCurrency(isGC ? 'SC' : 'GC')}
              className={`relative flex-shrink-0 w-[68px] h-[38px] rounded-full transition-all duration-300 active:scale-95 shadow-lg ${
                isGC
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
              }`}
            >
              {/* Sliding thumb */}
              <div
                className={`absolute top-[4px] transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  isGC ? 'left-[4px]' : 'left-[calc(100%-30px-4px)]'
                }`}
              >
                <CurrencyBadge type={selectedCurrency} size="sm" variant="white" className="w-[30px] h-[30px] text-[9px] rounded-full shadow-md" />
              </div>
            </button>
          </div>
        </div>

        {/* Jackpot ticker — integrated seamlessly below balance */}
        {!isGamePlayPage && (
          <button 
            onClick={() => navigate('/jackpot')}
            className="w-full flex items-center justify-center gap-3 px-3 pb-2 pt-0.5 pointer-events-auto active:opacity-70 transition-opacity"
          >
            {[
              { label: 'Grand', color: 'text-purple-400', amount: jackpots.grand.amount },
              { label: 'Major', color: 'text-red-400', amount: jackpots.major.amount },
              { label: 'Minor', color: 'text-green-400', amount: jackpots.minor.amount },
              { label: 'Mini', color: 'text-blue-400', amount: jackpots.mini.amount },
            ].map((jp) => (
              <div key={jp.label} className="flex items-center gap-1">
                <span className={`${jp.color} text-[9px] font-semibold uppercase tracking-wide`}>{jp.label}</span>
                <span className="text-foreground/80 text-[10px] font-medium tabular-nums">{formatJackpotAmount(jp.amount)}</span>
              </div>
            ))}
          </button>
        )}

        {/* Bottom fade-out edge — subtle separator */}
        {!isGamePlayPage && (
          <div className={`h-[1px] transition-colors duration-300 ${
            isGC 
              ? 'bg-gradient-to-r from-transparent via-amber-500/30 to-transparent' 
              : 'bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent'
          }`} />
        )}
      </header>

      {/* Desktop: Full Header — adapts when chat panel is open */}
      <header className="hidden lg:block bg-card border-b border-border w-full z-50 backdrop-blur-sm shadow-xl">

        <div className="w-full px-2 sm:px-3 lg:px-4 py-2 lg:py-3 max-w-full min-w-0">
          <div className="flex items-center min-w-0 gap-2 lg:gap-3">

            {/* Search icon button — opens overlay */}
            {shouldShowSearch && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMobileSearchOpen(true)}
                className="h-[46px] w-[46px] rounded-xl border border-border bg-muted/60 text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <Search className="h-5 w-5" />
              </Button>
            )}

            {/* Right side - Currency, Actions, Profile */}
            <div className="flex items-center gap-2 min-w-0 ml-auto">
              {/* Currency Slider */}
              <div className="flex-shrink min-w-0 overflow-hidden">
                <CurrencySlider />
              </div>

              {/* Get Coins Button — hidden when moved to sidebar */}
              {!buttonsInSidebar && (
                <Button 
                  onClick={openGetCoinsModal}
                  onPointerEnter={prefetchPackages}
                  className={`bg-muted/60 backdrop-blur-md border border-border hover:bg-muted text-foreground font-semibold rounded-xl h-[46px] flex-shrink-0 whitespace-nowrap shadow-lg shadow-black/10 transition-all justify-center ${
                    chatContext?.isOpen ? 'px-3 text-sm min-w-[5.5rem]' : 'px-4 lg:px-7 text-sm lg:text-[15px] min-w-[7rem]'
                  }`}
                >
                  Get Coins
                </Button>
              )}

              {/* Redeem Button — hidden when moved to sidebar */}
              {!buttonsInSidebar && (
                <Button 
                  onClick={() => setIsRedeemModalOpen(true)}
                  className={`font-semibold rounded-xl h-[46px] flex-shrink-0 whitespace-nowrap shadow-lg transition-all border justify-center ${
                    chatContext?.isOpen ? 'px-3 text-sm min-w-[5.5rem]' : 'px-4 lg:px-7 text-sm lg:text-[15px] min-w-[7rem]'
                  } ${
                    selectedCurrency === 'GC'
                      ? 'bg-gradient-to-b from-amber-400/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-500 text-white border-amber-400/30 shadow-amber-500/20'
                      : 'bg-gradient-to-b from-emerald-400/90 to-emerald-600/90 hover:from-emerald-400 hover:to-emerald-500 text-white border-emerald-400/30 shadow-emerald-500/20'
                  }`}
                >
                  Redeem
                </Button>
              )}

              {/* Chat Toggle (Desktop) */}
              {isAuthenticated && chatContext && (
                <Button
                  onClick={chatContext.toggleChat}
                  variant="ghost"
                  size="icon"
                  className={`relative h-[46px] w-[46px] rounded-xl border flex-shrink-0 transition-all ${
                    chatContext.isOpen
                      ? 'bg-primary/10 border-primary/30 text-primary'
                      : 'bg-muted/60 border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <MessageCircle className="h-5 w-5" />
                  {chatContext.unreadCount > 0 && !chatContext.isOpen && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none shadow-sm">
                      {chatContext.unreadCount > 99 ? '99+' : chatContext.unreadCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <RedeemModal 
        isOpen={isRedeemModalOpen} 
        onClose={() => setIsRedeemModalOpen(false)} 
      />

      {/* Mobile Search Overlay */}
      <MobileSearchOverlay 
        isOpen={isMobileSearchOpen} 
        onClose={() => setIsMobileSearchOpen(false)} 
      />
    </>
  );
};
